import { ArrowLeftRight, ArrowUpRight, Check, CheckCircle2, Compass, Eye, FileText, Globe, LayoutGrid, Lightbulb, Lock, MapPin, Send, ShieldCheck, Users, X, Zap } from "lucide-react";
import { useMemo, useState, type ReactNode } from "react";
import { CountryBadge } from "./ui";
import {
  conflictSafeWarning,
  cooperationConfidentialityLevels,
  cooperationRequestTypes,
  cooperationUrgencies,
  createCooperationRequest,
  defaultConflictCheckRequired,
  labelForRequestType,
  professionalResponsibilityNotice,
  rankCooperationTargets,
  validateCooperationRequestDraft,
} from "../lib/cooperation";
import type { CooperationRequest, CooperationRequestConfidentiality, CooperationRequestType, CooperationUrgency, Firm, LexData } from "../lib/types";

const COUNTRY_CODES: Record<string, string> = {
  Belgium: "BE", Germany: "DE", Switzerland: "CH", Sweden: "SE", Luxembourg: "LU", France: "FR",
  Netherlands: "NL", Italy: "IT", Spain: "ES", Poland: "PL", Austria: "AT", Portugal: "PT", Ireland: "IE", Denmark: "DK", Finland: "FI", Norway: "NO",
};
const countryCode = (jurisdiction: string) => COUNTRY_CODES[jurisdiction] ?? jurisdiction.slice(0, 2).toUpperCase();

const typeMeta: Record<CooperationRequestType, { tag: string; icon: typeof Eye }> = {
  ask_view: { tag: "LIGHT", icon: Eye },
  local_counsel: { tag: "JURISDICTION", icon: MapPin },
  co_counsel: { tag: "COLLAB", icon: Users },
  matter_referral: { tag: "CONTROLLED", icon: ArrowUpRight },
  expert_input: { tag: "SPECIALIST", icon: Lightbulb },
  jurisdiction_assistance: { tag: "ORIENTATION", icon: Compass },
  working_group_cooperation: { tag: "PROJECT", icon: LayoutGrid },
};

const selectClass = "min-h-11 w-full rounded-lg border border-line bg-surface px-3 text-[14px] text-ink outline-none focus:border-primary";
const stepIds: Record<number, string> = { 1: "coop-type", 2: "coop-summary", 3: "coop-target", 4: "coop-notice" };

export function CooperationRequestModal({
  data,
  defaultTargetFirm,
  defaultRequestType = "ask_view",
  onClose,
  onCreate,
}: {
  data: LexData;
  defaultTargetFirm?: Firm | null;
  defaultRequestType?: CooperationRequestType;
  onClose: () => void;
  onCreate: (request: CooperationRequest) => void;
}) {
  const [requestType, setRequestType] = useState<CooperationRequestType>(defaultRequestType);
  const [title, setTitle] = useState(defaultRequestType === "ask_view" ? "Could we have your view?" : "");
  const [shortSummary, setShortSummary] = useState("");
  const [targetFirmId, setTargetFirmId] = useState(defaultTargetFirm?.id ?? data.firms.find((firm) => firm.id !== data.viewerFirm?.id)?.id ?? "");
  const [targetUserId, setTargetUserId] = useState("");
  const [jurisdiction, setJurisdiction] = useState(defaultTargetFirm?.jurisdiction ?? data.viewerFirm?.jurisdiction ?? "");
  const [practiceAreaId, setPracticeAreaId] = useState(data.practiceAreas[0]?.id ?? "");
  const [urgency, setUrgency] = useState<CooperationUrgency>("normal");
  const [deadline, setDeadline] = useState("");
  const [confidentialityLevel, setConfidentialityLevel] = useState<CooperationRequestConfidentiality>("requester_and_target_only");
  const [allowFirmRouting, setAllowFirmRouting] = useState(true);
  const [conflictCheckRequired, setConflictCheckRequired] = useState(defaultConflictCheckRequired(defaultRequestType));
  const [activeStep, setActiveStep] = useState(1);
  const [error, setError] = useState<string | null>(null);

  const targetFirm = data.firms.find((firm) => firm.id === targetFirmId);
  const targetUsers = data.users.filter((user) => user.firmId === targetFirmId && user.active);
  const selectedPractice = data.practiceAreas.find((area) => area.id === practiceAreaId);
  const jurisdictions = useMemo(() => [...new Set(data.firms.map((firm) => firm.jurisdiction).concat(["EU", "Cross-border", "Global"]))].sort(), [data.firms]);
  const targetSuggestions = useMemo(
    () => rankCooperationTargets(data, { requestType, jurisdiction, practiceAreaIds: practiceAreaId ? [practiceAreaId] : [], targetFirmId }),
    [data, jurisdiction, practiceAreaId, requestType, targetFirmId],
  );
  const maxScore = Math.max(1, ...targetSuggestions.map((suggestion) => suggestion.score));
  const matchPct = (score: number) => Math.min(99, Math.max(40, Math.round((score / maxScore) * 96)));

  const steps = [
    { n: 1, label: "Type", complete: true },
    { n: 2, label: "Summary", complete: Boolean(title.trim() && shortSummary.trim() && jurisdiction && practiceAreaId) },
    { n: 3, label: "Target", complete: Boolean(targetFirmId) },
    { n: 4, label: "Notice", complete: Boolean(confidentialityLevel) },
  ];
  const readinessFields = [Boolean(requestType), Boolean(title.trim()), Boolean(shortSummary.trim()), Boolean(jurisdiction), Boolean(practiceAreaId), Boolean(targetFirmId), Boolean(confidentialityLevel)];
  const readiness = Math.round((readinessFields.filter(Boolean).length / readinessFields.length) * 100);

  const changeType = (value: CooperationRequestType) => {
    setRequestType(value);
    setConflictCheckRequired(defaultConflictCheckRequired(value));
    if (value === "ask_view" && !title.trim()) setTitle("Could we have your view?");
  };

  const goToStep = (n: number) => {
    setActiveStep(n);
    document.getElementById(stepIds[n])?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const submit = () => {
    const draft = {
      requestType, title, shortSummary,
      jurisdictionTags: jurisdiction ? [jurisdiction] : [],
      practiceAreaIds: practiceAreaId ? [practiceAreaId] : [],
      urgency, confidentialityLevel,
      requesterFirmId: data.viewerFirm?.id ?? "",
      requesterUserId: data.viewer.id,
      targetFirmId, targetUserId: targetUserId || null,
      status: "sent" as const, conflictCheckRequired, clientDisclosed: false, confidentialDetailsAdded: false,
      createdAt: Date.now(), updatedAt: Date.now(),
    };
    const validation = validateCooperationRequestDraft(draft);
    if (validation) { setError(validation); return; }
    try {
      onCreate(createCooperationRequest({
        requestType, title, shortSummary,
        jurisdictionTags: jurisdiction ? [jurisdiction] : [],
        practiceAreaIds: practiceAreaId ? [practiceAreaId] : [],
        urgency, confidentialityLevel,
        requesterFirmId: data.viewerFirm?.id ?? "",
        requesterUserId: data.viewer.id,
        targetFirmId, targetUserId: targetUserId || null, relatedMemberId: targetUserId || null,
        conflictCheckRequired, allowFirmRouting,
        deadline: deadline ? new Date(deadline).getTime() : null,
      }));
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "The request could not be created.");
    }
  };

  const TypeIcon = typeMeta[requestType].icon;
  const confidentialityLabel = cooperationConfidentialityLevels.find((item) => item.value === confidentialityLevel)?.label ?? "";
  const routingNote = targetUserId ? targetUsers.find((user) => user.id === targetUserId)?.name ?? "" : "Let the firm route internally";

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-3" role="dialog" aria-modal="true">
      <div className="flex max-h-[94vh] w-[min(1180px,96vw)] flex-col overflow-hidden rounded-2xl bg-surface shadow-soft">
        {/* Header */}
        <div className="flex items-start gap-3 border-b border-line px-6 py-5">
          <span className="grid h-12 w-12 shrink-0 place-items-center rounded-xl bg-primary text-white"><ArrowLeftRight className="h-5 w-5" /></span>
          <div className="min-w-0 flex-1">
            <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-primary">Expertise exchange</div>
            <h2 className="text-[24px] font-semibold leading-tight tracking-[-0.02em] text-ink">New expertise exchange request</h2>
            <p className="mt-1 max-w-2xl text-[14px] leading-6 text-secondary">Ask the network for expertise, recommendations, local counsel, co-counsel support or a referral.</p>
            <div className="mt-4 flex flex-wrap items-center gap-1.5">
              {steps.map((step, index) => (
                <div key={step.n} className="flex items-center gap-1.5">
                  <button type="button" onClick={() => goToStep(step.n)} className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-[13px] font-medium transition ${activeStep === step.n ? "border-primary bg-tint text-primary" : step.complete ? "border-goodBorder bg-goodBg text-good" : "border-line bg-surface text-secondary hover:bg-hover"}`}>
                    <span className={`grid h-5 w-5 place-items-center rounded-full text-[11px] font-semibold ${activeStep === step.n ? "bg-primary text-white" : step.complete ? "bg-good text-white" : "bg-divider text-secondary"}`}>{step.complete && activeStep !== step.n ? <Check className="h-3 w-3" /> : step.n}</span>
                    {step.label}
                  </button>
                  {index < steps.length - 1 ? <span className="h-px w-5 bg-line" /> : null}
                </div>
              ))}
            </div>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" className="grid h-9 w-9 shrink-0 place-items-center rounded-lg border border-line bg-surface text-secondary hover:bg-hover hover:text-ink"><X className="h-4 w-4" /></button>
        </div>

        {/* Body */}
        <div className="grid min-h-0 flex-1 lg:grid-cols-[minmax(0,1fr)_380px]">
          <div className="min-w-0 overflow-y-auto px-6 py-5">
            {error ? <div className="mb-4 rounded-lg border border-danger/30 bg-[#FFF7F4] px-3 py-2 text-sm text-danger">{error}</div> : null}

            {/* Step 1 — Type */}
            <section id="coop-type" className="scroll-mt-2">
              <div className="mb-3 flex items-center justify-between">
                <h3 className="flex items-center gap-2 text-[16px] font-semibold text-ink"><StepBadge n={1} /> Choose request type</h3>
                <span className="text-[12px] text-muted">Pick one</span>
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                {cooperationRequestTypes.map((item) => {
                  const meta = typeMeta[item.value];
                  const Icon = meta.icon;
                  const selected = requestType === item.value;
                  return (
                    <button key={item.value} type="button" onClick={() => changeType(item.value)} className={`relative rounded-xl border p-4 text-left transition ${selected ? "border-primary bg-tint" : "border-line bg-surface hover:bg-hover"}`}>
                      {selected ? <span className="absolute right-3 top-3 grid h-5 w-5 place-items-center rounded-full bg-primary text-white"><Check className="h-3 w-3" /></span> : null}
                      <div className="flex items-start gap-3">
                        <span className={`grid h-9 w-9 shrink-0 place-items-center rounded-lg ${selected ? "bg-primary text-white" : "bg-divider text-secondary"}`}><Icon className="h-4 w-4" /></span>
                        <div className="min-w-0">
                          <div className="text-[14.5px] font-semibold text-ink">{item.label}</div>
                          <div className="mt-1 text-[12.5px] leading-5 text-secondary">{item.description} <span className="ml-1 inline-block rounded bg-divider px-1.5 py-0.5 font-mono text-[10px] font-medium uppercase tracking-[0.04em] text-muted">{meta.tag}</span></div>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </section>

            {/* Step 2 — Summary */}
            <section id="coop-summary" className="mt-8 scroll-mt-2">
              <h3 className="mb-3 flex items-center gap-2 text-[16px] font-semibold text-ink"><StepBadge n={2} /> Add a responsibility-safe summary</h3>
              <div className="rounded-lg border border-warnBorder bg-warnBg/50 px-3 py-2.5 text-[13px] leading-6 text-[#7a5e16]"><strong>Keep it non-confidential.</strong> {conflictSafeWarning}</div>
              <div className="mt-4 grid gap-4">
                <Labeled label="Title" counter={`${title.length}/80`}>
                  <input maxLength={80} className={selectClass.replace("min-h-11", "min-h-12")} value={title} onChange={(event) => setTitle(event.target.value)} placeholder="Short title for the request" />
                </Labeled>
                <Labeled label="Short summary" counter={`${shortSummary.length}/320`}>
                  <textarea maxLength={320} className="min-h-28 w-full rounded-lg border border-line bg-surface px-3 py-2.5 text-[14px] leading-6 text-ink outline-none focus:border-primary" value={shortSummary} onChange={(event) => setShortSummary(event.target.value)} placeholder="Keep this summary non-confidential and suitable for an initial network request." />
                </Labeled>
                <div className="grid gap-4 sm:grid-cols-3">
                  <Labeled label="Jurisdiction"><select className={selectClass} value={jurisdiction} onChange={(event) => setJurisdiction(event.target.value)}>{jurisdictions.map((item) => <option key={item} value={item}>{item}</option>)}</select></Labeled>
                  <Labeled label="Practice area"><select className={selectClass} value={practiceAreaId} onChange={(event) => setPracticeAreaId(event.target.value)}>{data.practiceAreas.map((area) => <option key={area.id} value={area.id}>{area.name}</option>)}</select></Labeled>
                  <Labeled label="Deadline" hint="optional"><input type="date" className={selectClass} value={deadline} onChange={(event) => setDeadline(event.target.value)} /></Labeled>
                </div>
                <Labeled label="Urgency">
                  <div className="flex gap-0.5 rounded-lg bg-divider p-1">
                    {cooperationUrgencies.map((item) => (
                      <button key={item.value} type="button" onClick={() => setUrgency(item.value)} className={`flex-1 rounded-md px-3 py-2 text-[13px] font-medium transition ${urgency === item.value ? "bg-surface font-semibold text-ink shadow-sm" : "text-secondary hover:text-ink"}`}>{item.label}</button>
                    ))}
                  </div>
                </Labeled>
              </div>
            </section>

            {/* Step 3 — Target */}
            <section id="coop-target" className="mt-8 scroll-mt-2">
              <div className="mb-3 flex items-center justify-between">
                <h3 className="flex items-center gap-2 text-[16px] font-semibold text-ink"><StepBadge n={3} /> Target member</h3>
                <span className="text-[12px] text-muted">{targetSuggestions.length} suggested</span>
              </div>
              {targetSuggestions.length > 0 ? (
                <div className="mb-4 rounded-xl border border-divider bg-canvas p-3">
                  <div className="mb-2 flex items-center gap-1.5 font-mono text-[10.5px] font-semibold uppercase tracking-[0.1em] text-muted"><Zap className="h-3.5 w-3.5" /> Suggested matches · ranked for {selectedPractice?.name ?? "this request"}</div>
                  <div className="grid gap-2">
                    {targetSuggestions.slice(0, 3).map((suggestion) => {
                      const selected = suggestion.firm.id === targetFirmId;
                      const pct = matchPct(suggestion.score);
                      return (
                        <button key={suggestion.firm.id} type="button" onClick={() => setTargetFirmId(suggestion.firm.id)} className={`flex items-center gap-3 rounded-lg border px-3 py-2.5 text-left transition ${selected ? "border-primary bg-tint" : "border-line bg-surface hover:bg-hover"}`}>
                          <CountryBadge code={countryCode(suggestion.firm.jurisdiction)} size="md" />
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2"><span className="truncate text-[14px] font-semibold text-ink">{suggestion.firm.name}</span><span className="rounded bg-divider px-1.5 py-0.5 text-[11px] text-secondary">{suggestion.firm.jurisdiction}</span></div>
                            <div className="mt-0.5 truncate text-[12px] text-secondary">{suggestion.reasons.join(" · ")}</div>
                          </div>
                          <span className="text-[14px] font-semibold text-good">{pct}%</span>
                          <span className={`grid h-5 w-5 place-items-center rounded-full border ${selected ? "border-primary" : "border-line"}`}>{selected ? <span className="h-2.5 w-2.5 rounded-full bg-primary" /> : null}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ) : null}
              <div className="grid gap-4 sm:grid-cols-2">
                <Labeled label="Target firm"><select className={selectClass} value={targetFirmId} onChange={(event) => setTargetFirmId(event.target.value)}>{data.firms.filter((firm) => firm.id !== data.viewerFirm?.id).map((firm) => <option key={firm.id} value={firm.id}>{firm.name} · {firm.jurisdiction}</option>)}</select></Labeled>
                <Labeled label="Target person" hint="optional"><select className={selectClass} value={targetUserId} onChange={(event) => setTargetUserId(event.target.value)}><option value="">Let the firm route internally</option>{targetUsers.map((user) => <option key={user.id} value={user.id}>{user.name}</option>)}</select></Labeled>
              </div>
              <label className={`mt-3 flex min-h-12 cursor-pointer items-center gap-2.5 rounded-lg border px-3 text-[14px] ${allowFirmRouting ? "border-primary bg-tint" : "border-line bg-surface"}`}>
                <span className={`grid h-[18px] w-[18px] place-items-center rounded-[5px] border ${allowFirmRouting ? "border-primary bg-primary text-white" : "border-line bg-surface text-transparent"}`}><Check className="h-3 w-3" strokeWidth={3} /></span>
                <input type="checkbox" className="sr-only" checked={allowFirmRouting} onChange={(event) => setAllowFirmRouting(event.target.checked)} />
                Allow the target firm to route this internally
              </label>
            </section>

            {/* Step 4 — Notice */}
            <section id="coop-notice" className="mt-8 scroll-mt-2">
              <h3 className="mb-3 flex items-center gap-2 text-[16px] font-semibold text-ink"><StepBadge n={4} /> Professional responsibility notice</h3>
              <div className="grid gap-4 sm:grid-cols-2">
                <Labeled label="Confidentiality"><select className={selectClass} value={confidentialityLevel} onChange={(event) => setConfidentialityLevel(event.target.value as CooperationRequestConfidentiality)}>{cooperationConfidentialityLevels.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}</select></Labeled>
                <div className="rounded-lg border border-warnBorder bg-warnBg/50 px-3 py-2.5 text-[13px] leading-6 text-[#7a5e16]">{professionalResponsibilityNotice}</div>
              </div>
            </section>
          </div>

          {/* Live preview */}
          <aside className="hidden min-w-0 overflow-y-auto border-l border-line bg-canvas px-5 py-5 lg:block">
            <div className="mb-3 flex items-center gap-1.5 font-mono text-[11px] font-semibold uppercase tracking-[0.12em] text-secondary"><span className="h-2 w-2 rounded-full bg-primary" /> Live preview</div>
            <div className="rounded-xl border border-line bg-surface p-4">
              <span className="inline-flex items-center gap-1.5 rounded-md bg-tint px-2 py-1 font-mono text-[11px] font-medium text-primary"><TypeIcon className="h-3.5 w-3.5" /> {labelForRequestType(requestType)}</span>
              <div className="mt-2.5 text-[18px] font-semibold leading-snug text-ink">{title || "Your request title"}</div>
              <p className="mt-1 text-[13px] italic leading-5 text-muted">{shortSummary || "Your non-confidential summary will appear here."}</p>
              <div className="mt-3 flex flex-wrap gap-1.5">
                {jurisdiction ? <span className="inline-flex items-center gap-1 rounded-full border border-line bg-surface px-2 py-0.5 text-[11.5px] text-secondary"><Globe className="h-3 w-3" /> {jurisdiction}</span> : null}
                {selectedPractice ? <span className="inline-flex items-center gap-1 rounded-full border border-line bg-surface px-2 py-0.5 text-[11.5px] text-secondary"><FileText className="h-3 w-3" /> {selectedPractice.name}</span> : null}
                <span className="inline-flex items-center gap-1 rounded-full border border-line bg-infoBg px-2 py-0.5 text-[11.5px] text-navy"><Zap className="h-3 w-3" /> {cooperationUrgencies.find((item) => item.value === urgency)?.label}</span>
              </div>
              <div className="mt-4 flex items-start gap-2.5 border-t border-divider pt-3">
                <CountryBadge code={countryCode(targetFirm?.jurisdiction ?? "")} size="md" />
                <div className="min-w-0">
                  <div className="text-[14px] font-semibold text-ink">{targetFirm?.name ?? "Target firm"}</div>
                  <div className="text-[12px] text-secondary">{allowFirmRouting ? "May route internally" : "Direct"} · {routingNote}</div>
                </div>
              </div>
              <div className="mt-2 flex items-center gap-1.5 text-[12px] text-muted"><Lock className="h-3.5 w-3.5" /> {confidentialityLabel}</div>
            </div>
            <div className="mt-4 grid gap-2.5">
              <div className="flex items-start gap-2 text-[12.5px] leading-5 text-secondary"><ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-good" /> This request does not create voting points, participation bonus or referral ranking.</div>
              <div className="flex items-start gap-2 text-[12.5px] leading-5 text-secondary"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-good" /> No matter value, fee field or client-name field is included in the initial request.</div>
            </div>
            <div className="mt-4 rounded-xl border border-line bg-surface p-3">
              <div className="flex items-center justify-between text-[12.5px] font-semibold text-ink"><span className="font-mono uppercase tracking-[0.08em] text-secondary">Request readiness</span><span>{readiness}%</span></div>
              <div className="mt-2 h-2 overflow-hidden rounded-full bg-track"><div className="h-full rounded-full bg-primary" style={{ width: `${readiness}%` }} /></div>
            </div>
          </aside>
        </div>

        {/* Footer */}
        <div className="flex flex-wrap items-center gap-3 border-t border-line px-6 py-4">
          <span className="inline-flex items-center gap-1.5 text-[12.5px] text-secondary"><ShieldCheck className="h-4 w-4 text-good" /> Responsibility-safe · no fee tracking</span>
          <div className="ml-auto flex gap-2">
            <button type="button" onClick={onClose} className="inline-flex min-h-11 items-center rounded-lg border border-line bg-surface px-4 text-[14px] font-semibold text-ink hover:bg-hover">Cancel</button>
            <button type="button" onClick={submit} className="inline-flex min-h-11 items-center gap-2 rounded-lg bg-primary px-5 text-[14px] font-semibold text-white hover:bg-[#6a1925]"><Send className="h-4 w-4" /> Send request</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function StepBadge({ n }: { n: number }) {
  return <span className="grid h-6 w-6 place-items-center rounded-md bg-ink text-[12px] font-semibold text-white">{n}</span>;
}


function Labeled({ label, hint, counter, children }: { label: string; hint?: string; counter?: string; children: ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1.5 flex items-center justify-between">
        <span className="text-[13px] font-semibold text-ink">{label}{hint ? <span className="ml-1 font-normal text-muted">· {hint}</span> : null}</span>
        {counter ? <span className="font-mono text-[11px] text-muted">{counter}</span> : null}
      </span>
      {children}
    </label>
  );
}
